import {
  sameBrandReference,
  type DocumentAssetCatalog,
  type DocumentBrandReference,
} from '../document-assets/document-assets.models';
import type {
  NoticeCatalog,
  NoticeComposition,
} from './notice-fidelity.models';

export type NoticeDocumentSetting =
  | {
      section: 'branding';
      property: 'profile';
      value: DocumentBrandReference | null;
    }
  | { section: 'page'; property: 'size' | 'fontCode'; value: string }
  | {
      section: 'page';
      property: 'marginPt' | 'fontSizePt' | 'lineHeight';
      value: number;
    }
  | {
      section: 'branding';
      property:
        | 'logoCode'
        | 'watermarkCode'
        | 'accentColor'
        | 'headerText'
        | 'footerText';
      value: string;
    };

export interface NoticeDocumentChoice {
  code: string;
  label: string;
}

export type NoticeDocumentSettingResult =
  | { ok: true; composition: NoticeComposition }
  | { ok: false; message: string };

const hash = /^[a-f0-9]{64}$/;
const text = /^[\t\n\r\x20-\x7e]*$/;
const color = /^#[a-f0-9]{6}$/i;

/** Only the current proof contract's installed font can be selected. */
export function noticeDocumentFontChoices(
  catalog: NoticeCatalog,
): NoticeDocumentChoice[] {
  return registered(catalog.fonts, 'lato-v1')
    ? [{ code: 'lato-v1', label: 'Pinned Lato' }]
    : [];
}

export function noticeDocumentLogoChoices(
  catalog: NoticeCatalog,
): NoticeDocumentChoice[] {
  return [
    { code: 'none', label: 'None' },
    ...(registered(catalog.assets, 'synthetic-seal-v1')
      ? [{ code: 'synthetic-seal-v1', label: 'Synthetic seal' }]
      : []),
  ];
}

export function noticeDocumentWatermarkChoices(
  catalog: NoticeCatalog,
): NoticeDocumentChoice[] {
  return [
    { code: 'none', label: 'None' },
    ...(registered(catalog.assets, 'synthetic-draft-v1')
      ? [{ code: 'synthetic-draft-v1', label: 'Synthetic draft' }]
      : []),
  ];
}

/** A single bounded edit of the retained source, preserving every other member. */
export function applyNoticeDocumentSetting(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  setting: NoticeDocumentSetting,
  assets?: DocumentAssetCatalog | null,
): NoticeDocumentSettingResult {
  if (
    document.version !== 1 ||
    !['notice-fidelity-experimental', 'notice-document-context-proof'].includes(
      document.contractCode,
    )
  )
    return fail(
      'Document settings require a supported retained v1 composition.',
    );

  if (setting.section === 'branding' && setting.property === 'profile') {
    if (
      setting.value !== null &&
      (!assets?.canSelect ||
        !assets.brands.some((brand) =>
          sameBrandReference(brand.reference, setting.value),
        ))
    )
      return fail(
        'Choose an authorized exact brand profile version from the current asset library.',
      );
  } else if (
    document.branding.profile &&
    ((setting.section === 'page' && setting.property === 'fontCode') ||
      (setting.section === 'branding' &&
        !['headerText', 'footerText'].includes(setting.property)))
  )
    return fail(
      'The selected brand profile governs this setting. Remove the profile to edit the retained local branding.',
    );
  const issue =
    setting.section === 'branding' && setting.property === 'profile'
      ? undefined
      : settingIssue(catalog, setting);
  if (issue) return fail(issue);

  const next = structuredClone(document);
  if (setting.section === 'page') {
    switch (setting.property) {
      case 'size':
        next.page.size = setting.value as 'letter' | 'a4';
        break;
      case 'fontCode':
        next.page.fontCode = setting.value;
        break;
      case 'fontSizePt': {
        const previous = next.page.fontSizePt;
        next.page.fontSizePt = setting.value;
        for (const block of next.blocks)
          if (block.style?.fontSizePt === previous)
            block.style.fontSizePt = setting.value;
        break;
      }
      case 'marginPt':
      case 'lineHeight':
        next.page[setting.property] = setting.value;
        break;
    }
  } else if (setting.property === 'profile') {
    if (setting.value === null) delete next.branding.profile;
    else next.branding.profile = structuredClone(setting.value);
  } else next.branding[setting.property] = setting.value;

  const limit = Math.min(
    32768,
    Number.isInteger(catalog.limits.sourceBytes) &&
      catalog.limits.sourceBytes > 0
      ? catalog.limits.sourceBytes
      : 32768,
  );
  if (
    new TextEncoder().encode(JSON.stringify(next, null, 2)).byteLength > limit
  )
    return fail(
      'This edit exceeds the supported source byte limit; shorten or remove content first.',
    );
  return { ok: true, composition: next };
}

function settingIssue(
  catalog: NoticeCatalog,
  setting: NoticeDocumentSetting,
): string | undefined {
  if (setting.section === 'page') {
    switch (setting.property) {
      case 'size':
        return setting.value === 'letter' || setting.value === 'a4'
          ? undefined
          : 'Choose Letter or A4 portrait.';
      case 'fontCode':
        return noticeDocumentFontChoices(catalog).some(
          (choice) => choice.code === setting.value,
        )
          ? undefined
          : 'Choose the installed pinned Lato font from the current catalog.';
      case 'marginPt':
        return range(setting.value, 36, 72, 'margin in points');
      case 'fontSizePt':
        return range(setting.value, 8, 24, 'body size in points');
      case 'lineHeight':
        return range(setting.value, 1, 1.8, 'line height');
      default:
        return 'Choose a supported page setting.';
    }
  }
  if (setting.section !== 'branding')
    return 'Choose a supported document setting.';
  switch (setting.property) {
    case 'logoCode':
      return noticeDocumentLogoChoices(catalog).some(
        (choice) => choice.code === setting.value,
      )
        ? undefined
        : 'Choose None or the registered synthetic seal from the current catalog.';
    case 'watermarkCode':
      return noticeDocumentWatermarkChoices(catalog).some(
        (choice) => choice.code === setting.value,
      )
        ? undefined
        : 'Choose None or the registered synthetic draft from the current catalog.';
    case 'accentColor':
      return typeof setting.value === 'string' && color.test(setting.value)
        ? undefined
        : 'Use a six-digit RGB accent color, such as #183B56.';
    case 'headerText':
    case 'footerText': {
      const limit = setting.property === 'headerText' ? 100 : 160;
      return typeof setting.value === 'string' &&
        setting.value.length <= limit &&
        text.test(setting.value)
        ? undefined
        : `Use ${setting.property === 'headerText' ? 'header' : 'footer'} wording of at most ${limit} printable ASCII characters or line/tab whitespace.`;
    }
    default:
      return 'Choose a supported branding setting.';
  }
}

function registered(
  entries: readonly { code: string; sha256: string }[],
  code: string,
): boolean {
  const matches = entries.filter((entry) => entry.code === code);
  return matches.length === 1 && hash.test(matches[0].sha256);
}

function range(
  value: number,
  minimum: number,
  maximum: number,
  label: string,
): string | undefined {
  return Number.isFinite(value) && value >= minimum && value <= maximum
    ? undefined
    : `Choose a ${label} from ${minimum} through ${maximum}.`;
}

function fail(message: string): NoticeDocumentSettingResult {
  return { ok: false, message };
}
